import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, Upload, X, Loader2, RotateCcw, ImageIcon, Globe, EyeOff, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import { GALLERY } from "@/data/site";
import { fetchGallery, fetchHiddenBuiltins, fetchBuiltinOverrides, uploadGalleryPhoto, deleteGalleryPhoto, updateGalleryPhoto, updateBuiltin, hideBuiltin, restoreBuiltin, galleryImageUrl } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { EASE } from "@/components/motion/Reveal";

const UploadPanel = ({ passcode, onDone, onClose }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [title, setTitle] = useState("");
  const [sub, setSub] = useState("");
  const [progress, setProgress] = useState(null);
  const inputRef = useRef(null);

  const pick = (f) => {
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) return toast.error("JPEG, PNG or WebP only.");
    setFile(f);
    setPreview(URL.createObjectURL(f));
    if (!title) setTitle(f.name.replace(/\.\w+$/, "").replace(/[-_]+/g, " "));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a photo first.");
    if (!title.trim()) return toast.error("Give the photo a title.");
    setProgress(0);
    try {
      const compressed = await compressImage(file);
      const photo = await uploadGalleryPhoto(passcode, { file: compressed, title: title.trim(), sub: sub.trim() }, setProgress);
      toast.success("Photo added to the website");
      onDone(photo);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed. Try again.");
      setProgress(null);
    }
  };

  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.4, ease: EASE }}
      className="overflow-hidden"
      data-testid="gallery-upload-panel"
    >
      <div className="bg-panel/80 border border-brand/40 rounded-xl p-5 sm:p-6 mb-6 grid md:grid-cols-[220px_1fr] gap-5">
        <button
          type="button"
          onClick={() => inputRef.current.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files[0]); }}
          className="relative aspect-[4/3] rounded-lg border-2 border-dashed border-white/15 hover:border-brand/60 bg-ink/60 grid place-items-center overflow-hidden transition-colors"
          data-testid="gallery-upload-dropzone"
        >
          {preview ? (
            <img src={preview} alt="Preview" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <span className="text-center text-slate-500 text-xs px-4">
              <Upload className="mx-auto mb-2 text-brand-light" size={22} />
              Tap to choose a photo
              <br />or drag one here
            </span>
          )}
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => pick(e.target.files[0])} data-testid="gallery-upload-file-input" />
        </button>
        <div className="flex flex-col gap-3">
          <input className="field" placeholder="Title — e.g. Driveway, Strathdale" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} data-testid="gallery-upload-title-input" />
          <input className="field" placeholder="Caption (optional) — e.g. Oil stains gone in one pass" value={sub} onChange={(e) => setSub(e.target.value)} maxLength={160} data-testid="gallery-upload-caption-input" />
          <div className="flex items-center gap-3 mt-auto">
            <button type="submit" disabled={progress !== null} className="btn-primary !py-3 !text-base disabled:opacity-60" data-testid="gallery-upload-submit-btn">
              {progress !== null ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} strokeWidth={2.5} />}
              {progress !== null ? `Uploading ${progress}%` : "Add to website"}
            </button>
            <button type="button" onClick={onClose} className="btn-ghost !py-3 !text-base" data-testid="gallery-upload-cancel-btn">Cancel</button>
          </div>
          {progress !== null && (
            <div className="h-1 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-brand transition-[width] duration-200" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>
      </div>
    </motion.form>
  );
};

const EditCaption = ({ photo, onSave, onCancel }) => {
  const [title, setTitle] = useState(photo.title);
  const [sub, setSub] = useState(photo.sub || "");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!title.trim()) return toast.error("Title can't be empty.");
    setSaving(true);
    try {
      await onSave({ title: title.trim(), sub: sub.trim() });
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} className="absolute inset-0 z-10 bg-ink/95 backdrop-blur p-3 flex flex-col gap-2" data-testid={`admin-photo-edit-form-${photo.id}`}>
      <input className="field !py-2 !text-sm" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} placeholder="Title" autoFocus data-testid={`admin-photo-edit-title-${photo.id}`} />
      <input className="field !py-2 !text-sm" value={sub} onChange={(e) => setSub(e.target.value)} maxLength={160} placeholder="Caption" data-testid={`admin-photo-edit-caption-${photo.id}`} />
      <div className="mt-auto flex gap-2">
        <button type="submit" disabled={saving} className="btn-primary flex-1 !py-2 !text-sm" data-testid={`admin-photo-edit-save-${photo.id}`}>
          {saving ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} strokeWidth={3} />} Save
        </button>
        <button type="button" onClick={onCancel} className="btn-ghost !py-2 !px-3 !text-sm" data-testid={`admin-photo-edit-cancel-${photo.id}`}><X size={14} /></button>
      </div>
    </form>
  );
};

const PhotoTile = ({ photo, onRemove, onRestore, onEdit, editing, onStartEdit, onCancelEdit, hidden }) => (
  <motion.li
    layout
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.94 }}
    transition={{ duration: 0.3, ease: EASE }}
    className={`group relative rounded-xl overflow-hidden border ${hidden ? "border-white/5 opacity-60" : "border-white/10"} bg-panel aspect-[4/3]`}
    data-testid={`admin-photo-${photo.id}`}
  >
    <img src={photo.src} alt={photo.title} className={`absolute inset-0 w-full h-full object-cover ${hidden ? "grayscale" : ""}`} loading="lazy" />
    {editing && <EditCaption photo={photo} onSave={(p) => onEdit(photo, p)} onCancel={onCancelEdit} />}
    <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/30 to-transparent" />
    <span className={`absolute top-2.5 left-2.5 inline-flex items-center gap-1 px-2 py-1 rounded font-mono text-[9px] uppercase tracking-widest ${photo.builtin ? "bg-white/10 text-slate-300" : "bg-cyanglow/20 text-cyanglow"}`}>
      {hidden ? <EyeOff size={10} /> : <Globe size={10} />} {hidden ? "Hidden" : photo.builtin ? "Original" : "Uploaded"}
    </span>
    <div className="absolute inset-x-0 bottom-0 p-3 flex items-end justify-between gap-2">
      <div className="min-w-0">
        <div className="font-display font-extrabold uppercase text-white text-base leading-none truncate">{photo.title}</div>
        <div className="text-[11px] text-slate-400 truncate">{photo.sub}</div>
      </div>
      {hidden ? (
        <button onClick={() => onRestore(photo)} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-white/10 hover:bg-brand hover:text-white text-white text-xs font-bold uppercase tracking-wider transition-colors" data-testid={`admin-photo-restore-${photo.id}`}>
          <RotateCcw size={13} /> Restore
        </button>
      ) : (
        <span className="flex gap-1.5 shrink-0">
          <button onClick={() => onStartEdit(photo.id)} className="grid place-items-center w-9 h-9 rounded-md bg-ink/70 border border-white/10 text-slate-300 hover:bg-brand hover:border-brand hover:text-white transition-colors" aria-label="Edit caption" data-testid={`admin-photo-edit-${photo.id}`}>
            <Pencil size={14} />
          </button>
          <button onClick={() => onRemove(photo)} className="grid place-items-center w-9 h-9 rounded-md bg-ink/70 border border-white/10 text-slate-300 hover:bg-red-500 hover:border-red-500 hover:text-white transition-colors" aria-label="Remove photo" data-testid={`admin-photo-remove-${photo.id}`}>
            <Trash2 size={15} />
          </button>
        </span>
      )}
    </div>
  </motion.li>
);

const useAdminGallery = (passcode) => {
  const [uploaded, setUploaded] = useState([]);
  const [hidden, setHidden] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, h, o] = await Promise.all([fetchGallery(), fetchHiddenBuiltins(), fetchBuiltinOverrides()]);
      setUploaded(u);
      setHidden(h);
      setOverrides(o);
    } catch {
      toast.error("Couldn't load photos.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (photo) => {
    if (!window.confirm(`Remove "${photo.title}" from the website?`)) return;
    try {
      if (photo.builtin) setHidden(await hideBuiltin(passcode, photo.id));
      else {
        await deleteGalleryPhoto(passcode, photo.id);
        setUploaded((u) => u.filter((p) => p.id !== photo.id));
      }
      toast.success("Photo removed from the website");
    } catch {
      toast.error("Couldn't remove photo.");
    }
  };
  const edit = async (photo, payload) => {
    try {
      if (photo.builtin) {
        const saved = await updateBuiltin(passcode, photo.id, payload);
        setOverrides((o) => ({ ...o, [photo.id]: saved }));
      } else {
        const saved = await updateGalleryPhoto(passcode, photo.id, payload);
        setUploaded((u) => u.map((p) => (p.id === photo.id ? saved : p)));
      }
      setEditingId(null);
      toast.success("Caption updated");
    } catch {
      toast.error("Couldn't save caption.");
    }
  };
  const restore = async (photo) => {
    setHidden(await restoreBuiltin(passcode, photo.id));
    toast.success("Photo is back on the website");
  };

  const live = [
    ...GALLERY.filter((g) => !hidden.includes(g.id)).map((g) => ({ ...g, ...(overrides[g.id] || {}), builtin: true })),
    ...uploaded.map((p) => ({ id: p.id, src: galleryImageUrl(p), title: p.title, sub: p.sub, builtin: false })),
  ];
  const hiddenItems = GALLERY.filter((g) => hidden.includes(g.id)).map((g) => ({ ...g, builtin: true }));

  return { live, hiddenItems, loading, adding, setAdding, editingId, setEditingId, remove, restore, edit, addUploaded: (p) => setUploaded((u) => [...u, p]) };
};

export const AdminGallery = ({ passcode }) => {
  const { live, hiddenItems, loading, adding, setAdding, editingId, setEditingId, remove, restore, edit, addUploaded } = useAdminGallery(passcode);
  return (
    <div data-testid="admin-gallery">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <div className="font-display font-extrabold uppercase text-2xl text-white leading-none">Website photos</div>
          <div className="text-sm text-slate-500 mt-1" data-testid="admin-gallery-count">{live.length} photo{live.length === 1 ? "" : "s"} live in the gallery</div>
        </div>
        <button onClick={() => setAdding((a) => !a)} className="btn-primary !py-3 !text-base" data-testid="admin-gallery-add-btn">
          {adding ? <X size={16} strokeWidth={2.5} /> : <Plus size={16} strokeWidth={2.5} />} {adding ? "Close" : "Add photo"}
        </button>
      </div>

      <AnimatePresence>
        {adding && <UploadPanel passcode={passcode} onClose={() => setAdding(false)} onDone={(p) => { addUploaded(p); setAdding(false); }} />}
      </AnimatePresence>

      {loading ? (
        <div className="py-24 grid place-items-center text-slate-500"><Loader2 className="animate-spin" /></div>
      ) : (
        <>
          {live.length === 0 && (
            <div className="py-20 text-center text-slate-500" data-testid="admin-gallery-empty">
              <ImageIcon className="mx-auto mb-3 text-slate-600" size={36} />
              <div className="font-display font-bold uppercase text-lg text-slate-400">No photos on the website</div>
              <div className="text-sm">The gallery section is hidden until you add one.</div>
            </div>
          )}
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="admin-gallery-grid">
            <AnimatePresence>
              {live.map((p) => <PhotoTile key={p.id} photo={p} onRemove={remove} onRestore={restore} onEdit={edit} editing={editingId === p.id} onStartEdit={setEditingId} onCancelEdit={() => setEditingId(null)} />)}
            </AnimatePresence>
          </ul>
          {hiddenItems.length > 0 && (
            <div className="mt-10">
              <div className="eyebrow !text-slate-500 mb-4">Removed originals — restore any time</div>
              <ul className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="admin-gallery-hidden-grid">
                <AnimatePresence>
                  {hiddenItems.map((p) => <PhotoTile key={p.id} photo={p} hidden onRemove={remove} onRestore={restore} />)}
                </AnimatePresence>
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};
